import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CertBadge } from "./CertBadge";
import { toast } from "sonner";
import { AlertCircle, HelpCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const statusMap = {
  pending: "待审核",
  approved: "正常",
  rejected: "已拒绝"
};

const statusDescriptionMap = {
  pending: "您的备案申请正在审核中，请耐心等待。审核时间通常为1-3个工作日。",
  approved: "此域名备案审核已通过，状态正常。",
  rejected: "此域名备案申请已被拒绝。如有疑问，请联系管理员。"
};

const typeMap = {
  personal: "个人",
  business: "企业"
};

interface CertLookupProps {
  initialSearchTerm?: string;
}

export const CertLookup = ({ initialSearchTerm }: CertLookupProps) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || "");
  const [result, setResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialSearchTerm) {
      handleSearch();
    }
  }, [initialSearchTerm]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error("请输入备案号或域名");
      return;
    }

    setIsLoading(true);
    setError(null);
    console.log('Searching for:', searchTerm);
    
    try {
      const term = searchTerm.trim();
      const { data, error } = await supabase
        .from("cert_applications")
        .select("*")
        .or(`cert_number.eq.${term},domain.eq.${term}`)
        .not('domain', 'eq', 'PENDING')
        .maybeSingle();

      console.log('Search result:', data, 'error:', error);

      if (error) {
        setError("查询出错，请稍后重试");
        console.error("Error fetching cert info:", error);
        toast.error("查询失败，请稍后重试");
      } else if (!data) {
        setError(`抱歉，未找到与"${searchTerm}"相关的备案信息。\n该备案号或域名可能尚未在本平台进行备案。`);
      } else {
        setResult(data);
        toast.success("查询成功");
      }
    } catch (err) {
      console.error("Search error:", err);
      setError("查询出错，请稍后重试");
      toast.error("查询失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      <div className="flex space-x-2">
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="请输入备案号或域名"
          className="bg-white/10 text-white border-white/20 focus:border-blue-500/50 transition-colors"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button 
          onClick={handleSearch} 
          className="bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90 min-w-[80px] transition-all duration-300 hover:shadow-lg"
          disabled={isLoading}
        >
          {isLoading ? "查询中..." : "查询"}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-300 animate-fade-in">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="whitespace-pre-line">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {result && (
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-lg p-6 space-y-6 animate-fade-in border border-white/20 shadow-xl">
          <div className="space-y-4">
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <p className="text-sm text-blue-300">申请人/企业名称</p>
              <p className="text-white text-lg font-medium">{result.applicant_name}</p>
            </div>
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <p className="text-sm text-blue-300">域名</p>
              <p className="text-white text-lg font-medium">{result.domain.toUpperCase()}</p>
            </div>
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <p className="text-sm text-blue-300">备案号码</p>
              <p className="text-white text-lg font-medium">成人认证: {result.cert_number}</p>
            </div>
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <p className="text-sm text-blue-300">认证类型</p>
              <p className="text-white text-lg font-medium">{typeMap[result.applicant_type as keyof typeof typeMap]}</p>
            </div>
            <div className="flex items-center gap-2 transform hover:scale-[1.01] transition-all duration-300">
              <div>
                <p className="text-sm text-blue-300">认证状态</p>
                <p className="text-white text-lg font-medium">{statusMap[result.status as keyof typeof statusMap]}</p>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-white">
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-gray-900/95 border-white/10">
                    <p>{statusDescriptionMap[result.status as keyof typeof statusMap]}</p>
                    {result.status === 'rejected' && result.admin_notes && (
                      <p className="mt-2 text-red-300">拒绝原因: {result.admin_notes}</p>
                    )}
                    {result.reviewed_at && (
                      <p className="mt-2 text-sm text-gray-300">
                        审核时间: {new Date(result.reviewed_at).toLocaleString('zh-CN')}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <p className="text-sm text-blue-300">申请时间</p>
              <p className="text-white text-lg font-medium">
                {new Date(result.created_at).toLocaleString('zh-CN')}
              </p>
            </div>
          </div>
          
          <div className="pt-4 border-t border-white/10">
            <CertBadge certNumber={result.cert_number} domain={result.domain} />
          </div>
        </div>
      )}
    </div>
  );
};